import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, CheckCircle2, CreditCard, PenLine, Home, Lock, Upload, FileText, Trash2, Download, Landmark, FileSignature } from "lucide-react";

const gbp = (n) => `£${(Number(n) || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const FIELDS = {
  full_name: "", dob: "", phone: "", nationality: "",
  current_address: "", current_years: "", current_months: "",
  previous_address: "", previous_years: "", previous_months: "",
  prev_landlord_name: "", prev_landlord_phone: "", prev_landlord_email: "",
  employment_status: "Employed", employer: "", job_title: "", annual_income: "", employer_contact: "",
  receives_benefits: "No", benefits_detail: "", benefits_amount: "",
  has_guarantor: "No", guarantor_name: "", guarantor_email: "",
  right_to_rent: "", additional_notes: "",
};

const DOC_TYPES = [
  { key: "Proof of ID", desc: "Passport, driving licence or BRP" },
  { key: "Proof of address", desc: "Utility bill or council tax (last 3 months)" },
  { key: "3 months' payslips", desc: "Your last 3 monthly payslips" },
  { key: "3 months' bank statements", desc: "Your last 3 months of bank statements" },
];

export default function ApplicantPortal() {
  const { token } = useParams();
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState(FIELDS);
  const [consentCredit, setConsentCredit] = useState(false);
  const [consentData, setConsentData] = useState(false);
  const [signature, setSignature] = useState("");
  const [saving, setSaving] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payMsg, setPayMsg] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState("");
  const [obBusy, setObBusy] = useState(false);
  const [idBusy, setIdBusy] = useState(false);
  const [idMsg, setIdMsg] = useState("");
  const [tenancy, setTenancy] = useState(null);
  const [tenAck, setTenAck] = useState(false);
  const [tenSig, setTenSig] = useState("");
  const [tenBusy, setTenBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadFiles = useCallback(async () => {
    try { const { data } = await api.get(`/apply/${token}/files`); setFiles(data); } catch { /* ignore */ }
  }, [token]);

  const loadTenancy = useCallback(async () => {
    try { const { data } = await api.get(`/apply/${token}/tenancy`); setTenancy(data.available ? data : null); } catch { /* ignore */ }
  }, [token]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/apply/${token}`);
      setData(data);
      setForm({ ...FIELDS, ...(data.form || {}) });
      setSignature(data.signature || "");
      loadFiles();
      loadTenancy();
    } catch { setNotFound(true); }
  }, [token, loadFiles, loadTenancy]);

  useEffect(() => { load(); }, [load]);

  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 8) { setPayMsg("Your payment is taking a moment to confirm. If you completed it, there's nothing more to do — your agent will see it shortly. You can safely refresh this page."); return; }
    try {
      const { data: st } = await api.get(`/apply/${token}/payment-status/${sessionId}`);
      if (st.payment_status === "paid") { setPayMsg(""); toast.success("Holding deposit received — thank you!"); load(); return; }
      if (st.status === "expired") { setPayMsg("Payment session expired. Please try again."); return; }
      setPayMsg("Confirming your payment…");
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch {
      // Session not recorded yet (or transient) — keep trying quietly, then reassure.
      setPayMsg("Confirming your payment…");
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    }
  }, [token, load]);

  const pollIdStatus = useCallback(async (attempts = 0) => {
    if (attempts >= 8) { setIdMsg("Still processing — refresh in a moment to see the result."); return; }
    try {
      const { data: st } = await api.get(`/apply/${token}/id-status`);
      if (st.id_status === "Pass") { setIdMsg(""); toast.success("Identity verified — thank you!"); load(); return; }
      if (st.id_status === "Refer" || st.id_status === "Fail") { setIdMsg(""); load(); return; }
      setIdMsg("Confirming your identity check…");
      setTimeout(() => pollIdStatus(attempts + 1), 2500);
    } catch { setIdMsg(""); }
  }, [token, load]);

  useEffect(() => {
    const sid = sp.get("session_id");
    if (sid) { setPayMsg("Confirming your payment…"); pollStatus(sid); setSp({}, { replace: true }); return; }
    const ob = sp.get("ob");
    if (ob) {
      if (ob === "success") toast.success("Bank connected — income verified");
      else if (ob === "cancelled") toast.info("Bank connection cancelled");
      else toast.error("Could not connect your bank — please try again");
      load(); setSp({}, { replace: true }); return;
    }
    if (sp.get("id_check") === "return") { setIdMsg("Confirming your identity check…"); pollIdStatus(); setSp({}, { replace: true }); }
  }, [sp, pollStatus, pollIdStatus, setSp, load]);

  const saveDraft = async () => {
    setSaving(true);
    try { await api.post(`/apply/${token}`, { form, submit: false }); toast.success("Progress saved"); load(); }
    catch { toast.error("Could not save"); } finally { setSaving(false); }
  };

  const submit = async () => {
    if (!form.full_name) { toast.error("Please enter your full name"); return; }
    if (!consentCredit || !consentData) { toast.error("Please give the required consents"); return; }
    if (!signature.trim()) { toast.error("Please type your name to sign"); return; }
    setSaving(true);
    try {
      await api.post(`/apply/${token}`, { form: { ...form, consent_credit: true, consent_data: true }, signature: signature.trim(), submit: true });
      toast.success("Application submitted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); }
    finally { setSaving(false); }
  };

  const payDeposit = async (kind = "holding") => {
    setPaying(true);
    try {
      const { data: r } = await api.post(`/apply/${token}/checkout`, { origin_url: window.location.origin, kind });
      window.location.href = r.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start payment"); setPaying(false); }
  };

  const uploadDoc = async (e, label) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) { toast.error("File too large (max 25MB)"); return; }
    setUploading(label);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("label", label);
    try {
      await api.post(`/apply/${token}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${label} uploaded`);
      loadFiles();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(""); }
  };

  const deleteDoc = async (fid) => {
    try { await api.delete(`/apply/${token}/files/${fid}`); loadFiles(); }
    catch { toast.error("Could not remove"); }
  };

  const connectOpenBanking = async () => {
    setObBusy(true);
    try {
      const { data: r } = await api.post(`/apply/${token}/open-banking`, { origin_url: window.location.origin });
      window.location.href = r.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not connect"); setObBusy(false); }
  };

  const startIdVerification = async () => {
    setIdBusy(true);
    try {
      const { data: r } = await api.post(`/apply/${token}/id-verification`, {});
      window.location.href = r.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start ID verification"); setIdBusy(false); }
  };

  const signTenancy = async () => {
    if (!tenAck) { toast.error("Please confirm you've received the compliance documents"); return; }
    if (!tenSig.trim()) { toast.error("Please type your name to sign"); return; }
    setTenBusy(true);
    try {
      await api.post(`/apply/${token}/tenancy/sign`, { signature: tenSig.trim(), acknowledged: true });
      toast.success("Tenancy agreement signed");
      loadTenancy();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not sign"); }
    finally { setTenBusy(false); }
  };

  if (notFound) return <Centered><div className="text-center"><Lock className="mx-auto mb-3 text-slate-400" size={32} /><h1 className="text-xl font-bold text-slate-900">Link not valid</h1><p className="text-slate-500 mt-1 text-sm">This referencing link is invalid or has expired. Please contact your agent.</p></div></Centered>;
  if (!data) return <Centered><Loader2 className="animate-spin text-blue-600" size={28} /></Centered>;

  const submitted = data.status === "Submitted" || data.status === "Approved";
  const holdingPaid = data.holding_deposit_status === "paid";
  const approved = data.status === "Approved";
  const fullPaid = data.full_deposit_status === "paid";
  const docSlots = [
    ...DOC_TYPES,
    ...(form.receives_benefits === "Yes" ? [{ key: "Proof of benefits", desc: "Benefit award/entitlement letter", testid: "proof-benefits" }] : []),
  ].map((d) => ({ ...d, testid: d.testid || d.key.toLowerCase().replace(/[^a-z0-9]+/g, "-") }));

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-6 py-6">
          <div className="flex items-center gap-2 text-xs font-semibold text-blue-300 uppercase tracking-widest"><ShieldCheck size={14} /> Secure Tenant Referencing</div>
          <h1 className="font-display text-2xl font-bold mt-1">{data.company}</h1>
          <p className="text-slate-300 text-sm mt-1 flex items-center gap-1.5"><Home size={13} /> {data.address || data.property_ref}{data.rent_pcm ? ` · ${gbp(data.rent_pcm)}/mo` : ""}</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6" data-testid="applicant-portal">
        {/* GATE: pay the £75 holding deposit before the application opens */}
        {!holdingPaid ? (
          <Section title="Start your application" icon={CreditCard}>
            <p className="text-sm text-slate-600">To begin, please pay a small <b>holding deposit of {gbp(data.holding_deposit)}</b>. This reserves the property while we process your references. It's deducted from your first month's rent or refunded per the Tenant Fees Act.</p>
            <div className="flex items-center justify-between flex-wrap gap-3 mt-4">
              <p className="text-3xl font-bold text-slate-900">{gbp(data.holding_deposit)}</p>
              <button data-testid="pay-deposit-button" onClick={() => payDeposit("holding")} disabled={paying}
                className="bg-blue-700 hover:bg-blue-800 text-white rounded-lg px-6 py-3 text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
                {paying ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />} Pay {gbp(data.holding_deposit)} & start
              </button>
            </div>
            {payMsg && <p className="text-sm text-blue-700 mt-3" data-testid="pay-status">{payMsg}</p>}
          </Section>
        ) : (
        <>
        {submitted && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3" data-testid="applicant-submitted-banner">
            <CheckCircle2 className="text-emerald-600 shrink-0" size={20} />
            <div>
              <p className="font-semibold text-emerald-800">{approved ? "Application approved 🎉" : "Application submitted"}</p>
              <p className="text-sm text-emerald-700">{approved ? (fullPaid ? `Congratulations, ${data.name}! Your references passed and your deposit is paid in full — you're all set. Your agent will be in touch to finalise your tenancy.` : `Congratulations, ${data.name}! Your references passed. Please pay your tenancy deposit below to secure the property.`) : `Thank you, ${data.name}. Your agent is reviewing your references. You can no longer edit this form.`}</p>
            </div>
          </div>
        )}

        {/* Deposit — paid confirmation */}
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-2 text-sm text-emerald-700" data-testid="deposit-paid-badge">
          <CheckCircle2 size={16} /> {fullPaid
            ? `Full tenancy deposit of ${gbp(data.full_deposit)} paid — thank you, you're all set.`
            : `Holding deposit of ${gbp(data.holding_deposit)} paid — your application is open.`}
        </div>

        {/* Full deposit balance — only after approval */}
        {approved && data.deposit_balance > 0 && (
          <Section title="Tenancy deposit" icon={CreditCard}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-sm text-slate-600">Your references passed. Your tenancy deposit is one month's rent ({gbp(data.full_deposit)}). Your {gbp(data.holding_deposit)} holding deposit counts towards it — please pay the remaining balance to secure the property.</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{gbp(data.deposit_balance)} <span className="text-sm font-normal text-slate-400">balance due</span></p>
              </div>
              {fullPaid ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-700 px-4 py-2 text-sm font-semibold" data-testid="full-deposit-paid-badge"><CheckCircle2 size={16} /> Paid</span>
              ) : (
                <button data-testid="pay-full-deposit-button" onClick={() => payDeposit("deposit")} disabled={paying}
                  className="bg-blue-700 hover:bg-blue-800 text-white rounded-lg px-5 py-2.5 text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
                  {paying ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />} Pay deposit balance {gbp(data.deposit_balance)}
                </button>
              )}
            </div>
            {payMsg && <p className="text-sm text-blue-700 mt-3" data-testid="pay-status">{payMsg}</p>}
          </Section>
        )}

        {/* First month's rent — only once the tenancy is fully signed (Executed) */}
        {tenancy && tenancy.status === "Executed" && (
          data.tenancy_live ? (
            <Section title="Your tenancy is now live 🎉" icon={CheckCircle2}>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="tenancy-live-banner">
                <p className="font-semibold text-emerald-800">Welcome to your new home, {data.name}!</p>
                <p className="text-sm text-emerald-700 mt-1">Your first month's rent of {gbp(data.first_month_rent)} has been received and your tenancy is now live. Please <b>contact your agent for entry information and key collection</b>.</p>
              </div>
            </Section>
          ) : (
            <Section title="First month's rent" icon={CreditCard}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="text-sm text-slate-600">Your tenancy agreement is signed. Please pay your first month's rent to complete your move-in.</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{gbp(data.first_month_rent)}</p>
                </div>
                <button data-testid="pay-rent-button" onClick={() => payDeposit("rent")} disabled={paying}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-5 py-2.5 text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
                  {paying ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />} Pay first month's rent {gbp(data.first_month_rent)}
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-3">Paying by bank transfer or cash? Your agent can confirm receipt on their side.</p>
              {payMsg && <p className="text-sm text-blue-700 mt-3" data-testid="pay-status">{payMsg}</p>}
            </Section>
          )
        )}

        {/* Tenancy agreement — review compliance pack + e-sign */}
        {tenancy && (
          <Section title={`${tenancy.agreement_type || "Assured Tenancy"} agreement`} icon={FileSignature}>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 mb-4">
              <p className="text-sm text-amber-900 font-medium">Before you sign — please review these legal documents</p>
              <p className="text-xs text-amber-800 mt-0.5">Your landlord/agent must provide these under the Renters' Rights Act 2025. Please open each one.</p>
              <div className="mt-3 space-y-2" data-testid="compliance-docs">
                {(tenancy.compliance_docs || []).map((d, i) => (
                  <a key={i} href={d.builtin ? d.url : `${API}/apply/${token}/tenancy/compliance/${d.file_id}/download`} target="_blank" rel="noreferrer"
                    data-testid={`compliance-doc-${i}`} className="flex items-center justify-between rounded-lg bg-white border border-amber-100 px-3 py-2 hover:border-amber-300 transition-colors group">
                    <span className="flex items-center gap-2 text-sm text-slate-700"><ShieldCheck size={14} className="text-amber-500" /> {d.label}</span>
                    <Download size={14} className="text-slate-300 group-hover:text-amber-600" />
                  </a>
                ))}
              </div>
            </div>

            <a href={`${API}/apply/${token}/tenancy/pdf`} target="_blank" rel="noreferrer" data-testid="review-tenancy-pdf" className="inline-flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-800 mb-4"><FileText size={15} /> Read the full tenancy agreement (PDF)</a>

            {tenancy.tenant_signature ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3" data-testid="tenancy-signed-banner">
                <CheckCircle2 className="text-emerald-600 shrink-0" size={20} />
                <div><p className="font-semibold text-emerald-800">You've signed your tenancy agreement</p><p className="text-sm text-emerald-700">Signed as <b>{tenancy.tenant_signature}</b>. Your agent/landlord will countersign to complete it.{tenancy.status === "Executed" && " ✅ The agreement is now fully executed."}</p></div>
              </div>
            ) : (
              <div className="rounded-xl border border-slate-200 p-4">
                <label className="flex items-start gap-2.5 text-sm text-slate-700 mb-4 cursor-pointer">
                  <input type="checkbox" data-testid="tenancy-ack" checked={tenAck} onChange={(e) => setTenAck(e.target.checked)} className="mt-0.5" />
                  I confirm I have received and read the compliance documents above and the tenancy agreement, and I agree to its terms.
                </label>
                <label className="block text-sm font-medium text-slate-700 mb-1">Type your full name to sign</label>
                <input data-testid="tenancy-signature-input" value={tenSig} onChange={(e) => setTenSig(e.target.value)} placeholder="Your full legal name"
                  style={{ fontFamily: "'Segoe Script','Brush Script MT',cursive" }}
                  className="w-full rounded-lg border border-slate-300 px-4 py-3 text-lg mb-3 focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <button onClick={signTenancy} disabled={tenBusy} data-testid="sign-tenancy-button"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-5 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                  {tenBusy ? <Loader2 size={16} className="animate-spin" /> : <FileSignature size={16} />} Sign tenancy agreement
                </button>
              </div>
            )}
          </Section>
        )}

        {/* Open Banking — real TrueLayer connect */}
        <Section title="Verify your income (Open Banking)" icon={Landmark}>
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 flex items-start gap-3">
            <div className="flex-1">
              <p className="text-sm text-blue-900 font-medium">Connect your bank to fast-track your application ⚡</p>
              <p className="text-sm text-blue-800 mt-1">Securely share your income via Open Banking (powered by TrueLayer) to verify your affordability automatically — so you may not need to upload payslips or bank statements. It's read-only, bank-grade secure, and you can revoke access anytime.</p>
              {data.open_banking_connected && data.income_detected > 0 && (
                <p className="text-sm text-emerald-700 font-medium mt-2" data-testid="ob-income-detected">Verified income: {gbp(data.income_detected)}/mo</p>
              )}
            </div>
            {data.open_banking_connected ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-700 px-4 py-2 text-sm font-semibold shrink-0" data-testid="ob-connected-badge"><CheckCircle2 size={16} /> Connected</span>
            ) : (
              <button data-testid="connect-open-banking" onClick={connectOpenBanking} disabled={obBusy}
                className="bg-blue-700 hover:bg-blue-800 text-white rounded-lg px-4 py-2.5 text-sm font-semibold flex items-center gap-2 shrink-0 disabled:opacity-60">
                {obBusy ? <Loader2 size={16} className="animate-spin" /> : <Landmark size={16} />} Connect bank
              </button>
            )}
          </div>
        </Section>

        {/* Stripe Identity — ID document + selfie verification */}
        <Section title="Verify your identity" icon={ShieldCheck}>
          <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 flex items-start gap-3">
            <div className="flex-1">
              <p className="text-sm text-indigo-900 font-medium">Confirm your identity securely 🪪</p>
              <p className="text-sm text-indigo-800 mt-1">You'll be asked to photograph a government-issued ID (passport, driving licence or BRP) and take a quick selfie. This is handled securely by Stripe Identity and speeds up your Right to Rent and ID checks.</p>
              {idMsg && <p className="text-sm text-indigo-700 mt-2" data-testid="id-status-msg">{idMsg}</p>}
            </div>
            {data.id_status === "Pass" ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-700 px-4 py-2 text-sm font-semibold shrink-0" data-testid="id-verified-badge"><CheckCircle2 size={16} /> Verified</span>
            ) : (
              <button data-testid="start-id-verification" onClick={startIdVerification} disabled={idBusy}
                className="bg-indigo-700 hover:bg-indigo-800 text-white rounded-lg px-4 py-2.5 text-sm font-semibold flex items-center gap-2 shrink-0 disabled:opacity-60">
                {idBusy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} {data.id_status === "Refer" ? "Retry ID check" : "Verify ID"}
              </button>
            )}
          </div>
        </Section>

        {/* Documents upload — always available */}
        <Section title="Your documents" icon={Upload}>
          <p className="text-sm text-slate-600 mb-4">Please upload the documents below (PDF or images, up to 25MB each). You can upload more than one file per item.</p>
          <div className="space-y-3" data-testid="applicant-files">
            {docSlots.map((slot) => {
              const slotFiles = files.filter((f) => f.label === slot.key);
              return (
                <div key={slot.key} className="rounded-xl border border-slate-200 p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-2.5">
                      <span className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${slotFiles.length ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
                        {slotFiles.length ? <CheckCircle2 size={14} /> : <FileText size={13} />}
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{slot.key}{slot.optional && <span className="text-xs font-normal text-slate-400 ml-1">(optional)</span>}</p>
                        <p className="text-xs text-slate-400">{slot.desc}</p>
                      </div>
                    </div>
                    <label data-testid={`upload-${slot.testid}`} className={`inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-xs font-semibold cursor-pointer shrink-0 ${uploading === slot.key ? "bg-slate-200 text-slate-400" : "bg-slate-900 text-white hover:bg-slate-800"}`}>
                      {uploading === slot.key ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {slotFiles.length ? "Add another" : "Upload"}
                      <input type="file" className="hidden" onChange={(e) => uploadDoc(e, slot.key)} disabled={!!uploading} accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx" />
                    </label>
                  </div>
                  {slotFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {slotFiles.map((f) => (
                        <div key={f.file_id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2" data-testid={`file-${f.file_id}`}>
                          <div className="flex items-center gap-2 min-w-0"><FileText size={14} className="text-slate-400 shrink-0" /><span className="text-sm text-slate-700 truncate">{f.original_filename}</span></div>
                          <div className="flex items-center gap-1 shrink-0">
                            <a href={`${API}/apply/${token}/files/${f.file_id}/download`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-blue-600 p-1.5" title="Download"><Download size={14} /></a>
                            <button onClick={() => deleteDoc(f.file_id)} data-testid={`delete-file-${f.file_id}`} className="text-slate-400 hover:text-red-500 p-1.5" title="Remove"><Trash2 size={14} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Section>

        <fieldset disabled={submitted} className={submitted ? "opacity-60 pointer-events-none space-y-6" : "space-y-6"}>
          <Section title="Personal details">
            <Grid>
              <Field label="Full name" testid="f-full_name" value={form.full_name} onChange={(v) => set("full_name", v)} />
              <Field label="Date of birth" type="date" testid="f-dob" value={form.dob} onChange={(v) => set("dob", v)} />
              <Field label="Phone" testid="f-phone" value={form.phone} onChange={(v) => set("phone", v)} />
              <Field label="Nationality" testid="f-nationality" value={form.nationality} onChange={(v) => set("nationality", v)} />
            </Grid>
          </Section>

          <Section title="Address history">
            <Grid>
              <Field label="Current address" full testid="f-current_address" value={form.current_address} onChange={(v) => set("current_address", v)} />
              <DurationField label="Time at current address" testid="current" years={form.current_years} months={form.current_months}
                onYears={(v) => set("current_years", v)} onMonths={(v) => set("current_months", v)} />
              <Field label="Previous address (if < 3 years at current)" full testid="f-previous_address" value={form.previous_address} onChange={(v) => set("previous_address", v)} />
              <DurationField label="Time at previous address" testid="previous" years={form.previous_years} months={form.previous_months}
                onYears={(v) => set("previous_years", v)} onMonths={(v) => set("previous_months", v)} />
            </Grid>
            <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
              <p className="text-sm font-semibold text-slate-800 mb-3">Previous landlord / letting agent</p>
              <p className="text-xs text-slate-400 mb-3">Contact details for a reference from your previous landlord or agent.</p>
              <Grid>
                <Field label="Name" testid="f-prev_landlord_name" value={form.prev_landlord_name} onChange={(v) => set("prev_landlord_name", v)} placeholder="Landlord or agency name" />
                <Field label="Phone" testid="f-prev_landlord_phone" value={form.prev_landlord_phone} onChange={(v) => set("prev_landlord_phone", v)} placeholder="e.g. 07123 456789" />
                <Field label="Email" full testid="f-prev_landlord_email" type="email" value={form.prev_landlord_email} onChange={(v) => set("prev_landlord_email", v)} placeholder="landlord@example.com" />
              </Grid>
            </div>
          </Section>

          <Section title="Employment & income">
            <Grid>
              <SelectField label="Employment status" testid="f-employment_status" value={form.employment_status} onChange={(v) => set("employment_status", v)}
                options={["Employed", "Self-employed", "Student", "Retired", "Unemployed"]} />
              <Field label="Employer" testid="f-employer" value={form.employer} onChange={(v) => set("employer", v)} />
              <Field label="Job title" testid="f-job_title" value={form.job_title} onChange={(v) => set("job_title", v)} />
              <Field label="Annual income (£)" type="number" testid="f-annual_income" value={form.annual_income} onChange={(v) => set("annual_income", v)} />
              <Field label="Employer contact (name/email)" full testid="f-employer_contact" value={form.employer_contact} onChange={(v) => set("employer_contact", v)} />
              <SelectField label="Do you receive any benefits?" testid="f-receives_benefits" value={form.receives_benefits} onChange={(v) => set("receives_benefits", v)} options={["No", "Yes"]} />
              {form.receives_benefits === "Yes" && <>
                <Field label="Which benefits?" testid="f-benefits_detail" value={form.benefits_detail} onChange={(v) => set("benefits_detail", v)} placeholder="e.g. Universal Credit, Housing Benefit" />
                <Field label="Benefit amount (£ / month)" type="number" testid="f-benefits_amount" value={form.benefits_amount} onChange={(v) => set("benefits_amount", v)} />
              </>}
            </Grid>
          </Section>

          <Section title="Guarantor (optional)">
            <Grid>
              <SelectField label="Do you have a guarantor?" testid="f-has_guarantor" value={form.has_guarantor} onChange={(v) => set("has_guarantor", v)} options={["No", "Yes"]} />
              {form.has_guarantor === "Yes" && <>
                <Field label="Guarantor name" testid="f-guarantor_name" value={form.guarantor_name} onChange={(v) => set("guarantor_name", v)} />
                <Field label="Guarantor email" testid="f-guarantor_email" value={form.guarantor_email} onChange={(v) => set("guarantor_email", v)} />
              </>}
            </Grid>
          </Section>

          <Section title="Right to Rent">
            <Field label="Share code or ID reference" full testid="f-right_to_rent" value={form.right_to_rent} onChange={(v) => set("right_to_rent", v)} placeholder="Home Office share code, or note that you'll provide your passport/BRP" />
            <Field label="Anything else your agent should know?" full textarea testid="f-additional_notes" value={form.additional_notes} onChange={(v) => set("additional_notes", v)} />
          </Section>

          <Section title="Consent & declaration" icon={PenLine}>
            <label className="flex items-start gap-2.5 text-sm text-slate-700 mb-2 cursor-pointer">
              <input type="checkbox" data-testid="consent-credit" checked={consentCredit} onChange={(e) => setConsentCredit(e.target.checked)} className="mt-0.5" />
              I consent to a credit check and referencing checks being carried out on this application.
            </label>
            <label className="flex items-start gap-2.5 text-sm text-slate-700 mb-4 cursor-pointer">
              <input type="checkbox" data-testid="consent-data" checked={consentData} onChange={(e) => setConsentData(e.target.checked)} className="mt-0.5" />
              I confirm the information provided is true and I consent to it being processed in line with the agent's privacy policy.
            </label>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Type your full name to sign</label>
              <input data-testid="signature-input" value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Your full legal name"
                style={{ fontFamily: "'Segoe Script','Brush Script MT',cursive" }}
                className="w-full rounded-lg border border-slate-300 px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-blue-600" />
              <p className="text-xs text-slate-400 mt-1">Typing your name acts as your electronic signature.</p>
            </div>
          </Section>

          <div className="flex gap-3">
            <button data-testid="save-draft-button" onClick={saveDraft} disabled={saving}
              className="border border-slate-300 text-slate-700 hover:bg-white rounded-lg px-5 py-2.5 text-sm font-semibold disabled:opacity-60">Save progress</button>
            <button data-testid="submit-application-button" onClick={submit} disabled={saving}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-5 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Submit application
            </button>
          </div>
        </fieldset>
        </>
        )}
        <p className="text-center text-xs text-slate-400 pt-4">Powered by PinPoint · your data is transmitted securely.</p>
      </main>
    </div>
  );
}

const Centered = ({ children }) => <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6">{children}</div>;
const Grid = ({ children }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;

const Section = ({ title, icon: Icon, children }) => (
  <section className="bg-white rounded-xl border border-slate-200 p-5">
    <h2 className="font-display text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">{Icon && <Icon size={16} className="text-blue-600" />}{title}</h2>
    {children}
  </section>
);

const Field = ({ label, value, onChange, type = "text", full, textarea, placeholder, testid }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
    {textarea
      ? <textarea data-testid={testid} rows={2} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
      : <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />}
  </div>
);

const SelectField = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const DurationField = ({ label, testid, years, months, onYears, onMonths }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
    <div className="flex items-center gap-2">
      <div className="flex-1">
        <input data-testid={`f-${testid}_years`} type="number" min="0" value={years} onChange={(e) => onYears(e.target.value)} placeholder="Years"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
        <span className="text-xs text-slate-400 mt-0.5 block">Years</span>
      </div>
      <div className="flex-1">
        <input data-testid={`f-${testid}_months`} type="number" min="0" max="11" value={months} onChange={(e) => onMonths(e.target.value)} placeholder="Months"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
        <span className="text-xs text-slate-400 mt-0.5 block">Months</span>
      </div>
    </div>
  </div>
);
